import { useEffect, useRef } from 'react';
import {
  useCreateBlockNote, SuggestionMenuController, getDefaultReactSlashMenuItems,
  FormattingToolbarController, FormattingToolbar, getFormattingToolbarItems,
} from '@blocknote/react';
import { BlockNoteView } from '@blocknote/mantine';
import { createMathAssist } from '@harvus/shared';
import { B, radius } from '../../constants/brand';

// =============================================================
// NoteBodyEditor — the BlockNote editor on its own, with none of the composer
// chrome (no date, no author, no attachments, no Post button).
//
// `RichNoteEditor` (Notes.jsx) is the whole "write a note" form; this is the
// BODY of one, for a surface that owns the rest of the form itself. The Notes
// capture page (pages/GlobalNotes.jsx) is the reason it exists: its brain-dump
// box and every routed note row were plain <textarea>s, so a bullet list or a
// heading typed there filed onto the deal as literal "- item" text — the one
// notes surface in the app without the formatting every other note has.
//
// It is UNCONTROLLED (the `FieldBoundaryEditor` shape): `initialMarkdown` /
// `initialContent` seed it once, and every change is reported through
// `onChange({ content, markdown, isEmpty })` — `content` is the BlockNote JSON
// the notes API stores, `markdown` is the same document as Markdown (what the
// AI split reads, and what a task's plain-text description is made from). The
// editor instance is exposed through `editorRef` so a parent can insert text it
// obtained elsewhere (a handwriting scan) without re-seeding.
//
// The seed is REPORTED as soon as it lands, so a parent always holds the JSON
// for an untouched row — otherwise a section the AI routed and nobody edited
// would file through the Markdown fallback while its neighbour filed as JSON.
//
// `onChange` ALSO carries `html` (via `blocksToHTMLLossy`) — additive, and
// every existing caller destructures only the fields it wants, so this costs
// nothing to callers that only ever read `content`/`markdown`. It exists for
// Mail's rich-text compose (frontend/src/pages/Mail.jsx), which needs real
// HTML for Graph's message body rather than Markdown.
//
// `resolveFileUrl`, when passed, is BlockNote's own hook for turning a stored
// file reference into a link at render time — the Notes app stores
// `harvus-file://notebook/<id>` and resolves it to a short read link
// (notebook/files.js). `children` render inside the view (extra toolbars).
//
// `mathAssist` turns on the inline calculator: type `1,200 * 12 =` and the
// answer is written after the "=" (shared/src/noteMath.ts). The Notes app
// passes it; the capture page and Mail do not.
//
// `formattingToolbarExtras`, when passed, are buttons appended to BlockNote's
// own formatting toolbar (the bar that pops over a selected block) — the Notes
// app adds "Convert handwriting to text" for a file block there. Absent, the
// default toolbar renders exactly as before.
//
// `collaboration`, when passed, is BlockNote's own co-editing option — the Notes
// app (pages/NotesApp.jsx) passes the Yjs fragment from notebook/collab.js.
//
// `uploadFile`, when passed, is forwarded straight to `useCreateBlockNote` —
// existing callers pass nothing and get whatever BlockNote's own default
// (no image upload at all) already did, unchanged. Mail's compose editor is
// the first caller to pass one, turning a dropped/pasted image into a data:
// URI so it can go out in the message body with no server-side upload.
// =============================================================

const BN_THEME = {
  light: {
    colors: {
      editor: { text: '#1a2a32', background: '#ffffff' },
      menu: { text: '#1a2a32', background: '#ffffff' },
      tooltip: { text: '#ffffff', background: '#234c5a' },
      hovered: { text: '#1a2a32', background: '#eef5f4' },
      selected: { text: '#234c5a', background: '#d4ece8' },
      disabled: { text: '#7a9aaa', background: '#f5f7f6' },
      shadow: 'rgba(0,0,0,0.12)',
      border: '#dde8e5',
      sideMenu: '#7a9aaa',
    },
    borderRadius: 6,
    fontFamily: 'inherit',
  },
};

const isBlockNoteJSON = (content) => {
  if (!content) return false;
  const t = String(content).trim();
  return t.startsWith('[') && t.endsWith(']');
};

// Plain-text reading of a document, for an editor build without the Markdown
// exporter — never lets `markdown` come back undefined.
const blocksToText = (blocks) => (blocks || []).map(b => {
  const own = Array.isArray(b.content) ? b.content.map(c => c?.text || '').join('') : '';
  const kids = b.children?.length ? `\n${blocksToText(b.children)}` : '';
  return own + kids;
}).join('\n');

export const isDocumentEmpty = (blocks) =>
  !blocks || blocks.length === 0 || (blocks.length === 1 && (blocks[0].content?.length ?? 0) === 0 && !(blocks[0].children?.length));

/** Insert parsed blocks at the end of the document, replacing a lone empty paragraph. */
export const insertBlocksAtEnd = (editor, blocks) => {
  if (!editor || !blocks?.length) return;
  const doc = editor.document;
  if (isDocumentEmpty(doc)) editor.replaceBlocks(doc, blocks);
  else editor.insertBlocks(blocks, doc[doc.length - 1], 'after');
};

/** Insert Markdown (falling back to one paragraph per line) at the end. */
export const insertMarkdownAtEnd = async (editor, markdown) => {
  if (!editor || !markdown?.trim()) return false;
  try {
    const blocks = await editor.tryParseMarkdownToBlocks(markdown);
    if (blocks?.length) { insertBlocksAtEnd(editor, blocks); return true; }
  } catch { /* fall through to plain lines */ }
  insertBlocksAtEnd(editor, markdown.split('\n').map(line => ({ type: 'paragraph', content: line || '' })));
  return true;
};

export const NoteBodyEditor = ({
  initialMarkdown, initialContent, onChange, editorRef, minHeight = 120, autoFocus = false, style, uploadFile,
  collaboration, resolveFileUrl, children, mathAssist = false, formattingToolbarExtras,
}) => {
  const initialBlocks = (() => {
    if (!isBlockNoteJSON(initialContent)) return undefined;
    try { const b = JSON.parse(initialContent); return Array.isArray(b) && b.length ? b : undefined; } catch { return undefined; }
  })();
  // `collaboration` ({ fragment, user, provider }) makes the Yjs fragment the
  // document: no seed is applied (the fragment already holds the note), and
  // edits flow through Yjs to the Notes co-editing provider.
  const editor = useCreateBlockNote(collaboration
    ? { collaboration, uploadFile, resolveFileUrl }
    : { initialContent: initialBlocks, uploadFile, resolveFileUrl });
  const seq = useRef(0);
  const math = useRef(null);
  if (mathAssist && !math.current) math.current = createMathAssist();

  useEffect(() => { if (editorRef) editorRef.current = editor; }, [editor, editorRef]);

  // Report the live document; the Markdown/HTML exports are async, so a
  // stale resolution (a fast keystroke after a slow one) is dropped by
  // sequence.
  const report = async () => {
    if (!editor || !onChange) return;
    const mine = ++seq.current;
    const blocks = editor.document;
    let markdown;
    try {
      markdown = typeof editor.blocksToMarkdownLossy === 'function'
        ? await editor.blocksToMarkdownLossy(blocks)
        : blocksToText(blocks);
    } catch { markdown = blocksToText(blocks); }
    let html;
    try {
      html = typeof editor.blocksToHTMLLossy === 'function' ? await editor.blocksToHTMLLossy(blocks) : '';
    } catch { html = ''; }
    if (mine !== seq.current) return;
    onChange({ content: JSON.stringify(blocks), markdown: String(markdown || ''), html: String(html || ''), isEmpty: isDocumentEmpty(blocks) });
  };

  // Seed once from Markdown (a routed section, a scan) or legacy HTML, then
  // report so the parent holds the JSON for an untouched document.
  useEffect(() => {
    if (!editor) return;
    let cancelled = false;
    (async () => {
      try {
        if (collaboration || initialBlocks) { /* already loaded */ }
        else if (initialMarkdown?.trim()) {
          const blocks = await editor.tryParseMarkdownToBlocks(initialMarkdown);
          if (!cancelled && blocks?.length) editor.replaceBlocks(editor.document, blocks);
        } else if (initialContent?.trim()) {
          const blocks = editor.tryParseHTMLToBlocks(initialContent);
          if (!cancelled && blocks?.length) editor.replaceBlocks(editor.document, blocks);
        }
      } catch { /* leave the editor empty so the person can type */ }
      if (!cancelled) {
        report();
        if (autoFocus) { try { editor.focus(); } catch { /* not mounted yet */ } }
      }
    })();
    return () => { cancelled = true; };
  }, [editor]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      className="harvus-note-body"
      style={{
        border: `1px solid ${B.border}`, borderRadius: radius.md, background: B.white, overflow: 'hidden',
        // Read by GlobalStyles so the typing area fills the box (feedback #588).
        '--harvus-note-min-height': typeof minHeight === 'number' ? `${minHeight}px` : minHeight,
        ...style,
      }}
    >
      <BlockNoteView
        editor={editor}
        theme={BN_THEME}
        slashMenu={false}
        formattingToolbar={!formattingToolbarExtras}
        onChange={() => { if (math.current) math.current(editor); report(); }}
        style={{ minHeight, width: '100%' }}
      >
        <SuggestionMenuController
          triggerCharacter="/"
          getItems={async query => getDefaultReactSlashMenuItems(editor).filter(
            item => !query || item.title.toLowerCase().includes(query.toLowerCase())
          )}
        />
        {formattingToolbarExtras && (
          <FormattingToolbarController
            formattingToolbar={() => (
              <FormattingToolbar>{[...getFormattingToolbarItems(), ...formattingToolbarExtras]}</FormattingToolbar>
            )}
          />
        )}
        {children}
      </BlockNoteView>
    </div>
  );
};
